import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Psychology as PsychologyIcon,
  Add as AddIcon,
  CheckCircle as CompleteIcon,
  Schedule as ActiveIcon,
  Notes as NotesIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import * as supervisorApi from '../../services/supervisorApi';
import { setAuthToken } from '../../services/api';
import therapistStyles from '../therapist/styles/therapistStyles';

function ConsultationHistory({ token }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get clientId from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const clientIdFromUrl = searchParams.get('clientId');
  
  // State
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);

  // Load consultations
  useEffect(() => {
    // Only load if we have a token
    if (token) {
      // Ensure token is set in API service
      setAuthToken(token);
      loadConsultations();
      loadStatistics();
    }
  }, [token, page, rowsPerPage, statusFilter, clientIdFromUrl]);

  const loadConsultations = async () => {
    try {
      setLoading(true);
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      // Add clientId filter if present in URL
      if (clientIdFromUrl) {
        params.clientId = clientIdFromUrl;
      }

      const response = await supervisorApi.getConsultationHistory(params, token);
      console.log('Consultation history response:', response);
      setConsultations(response.consultations);
      setTotalCount(response.pagination.total);
      
      // If filtering by client, get client info
      if (clientIdFromUrl && response.consultations.length > 0) {
        const firstConsultation = response.consultations[0];
        if (firstConsultation.clientId) {
          setClientInfo(firstConsultation.clientId);
        }
      }
    } catch (error) {
      console.error('Error loading consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      // If filtering by client, pass clientId to get client-specific statistics
      const params = clientIdFromUrl ? { clientId: clientIdFromUrl } : {};
      const response = await supervisorApi.getConsultationStatistics(token, params);
      setStatistics(response.statistics);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewConsultation = (consultationId) => {
    navigate(`/supervisor/consultation/${consultationId}`);
  };

  const handleStartNewConsultation = () => {
    // Navigate back to clients tab
    navigate('/therapist/dashboard', { state: { activeTab: 0 } });
  };

  const handleBack = () => {
    const state = location.state;

    if (state?.returnTo === 'clientDashboard' || state?.returnTo === 'clientConversation') {
      // Navigate back to therapist dashboard clients tab with client selection and tab
      navigate('/therapist/dashboard?tab=clients', {
        state: {
          selectClient: state.client || { _id: state.clientId, name: state.clientName },
          openTab: state.tab || 0, // 0 for dashboard, 4 for conversation
          conversationId: state.conversationId // Pass conversation ID to restore specific conversation
        }
      });
    } else {
      // Default: go to therapist dashboard
      navigate('/therapist/dashboard');
    }
  };

  // Filter consultations based on search term
  const filteredConsultations = consultations.filter(consultation => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      consultation.clientId?.firstName?.toLowerCase().includes(searchLower) ||
      consultation.clientId?.lastName?.toLowerCase().includes(searchLower) ||
      consultation.clientId?.email?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'completed':
        return 'success';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <ActiveIcon />;
      case 'completed':
        return <CompleteIcon />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {location.state?.returnTo && (
            <IconButton onClick={handleBack} sx={{ color: '#42A5F5' }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box>
            <Typography variant="h5" display="flex" alignItems="center" gap={1} sx={{ fontWeight: 600, color: '#1a1a1a' }}>
              <PsychologyIcon sx={{ color: '#42A5F5' }} />
              AI Supervisor Consultations
              {clientInfo && ` - ${clientInfo.firstName} ${clientInfo.lastName}`}
            </Typography>
            {clientInfo && !location.state?.returnTo && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4.5 }}>
                <Button
                  size="small"
                  onClick={() => {
                    // Remove clientId from URL and reload
                    navigate('/therapist/dashboard?tab=consultations');
                  }}
                  sx={{ ml: 1 }}
                >
                  Clear Filter
                </Button>
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleStartNewConsultation}
          sx={{
            ...therapistStyles.primaryButton,
            py: 1,
            px: 3
          }}
        >
          New Consultation
        </Button>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={therapistStyles.statsCard}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Total Consultations
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  {statistics.totalConsultations}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={therapistStyles.statsCard}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Active
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF8C00' }}>
                  {statistics.activeConsultations}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={therapistStyles.statsCard}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Clients Consulted
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  {statistics.uniqueClientsConsulted}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={therapistStyles.statsCard}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Avg Duration
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  {statistics.averageConsultationDuration} min
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ 
        p: 2, 
        mb: 2,
        ...therapistStyles.mainCard
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                startAdornment={<FilterIcon sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Consultations Table */}
      <TableContainer component={Paper} sx={therapistStyles.tableContainer}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead sx={therapistStyles.tableHeader}>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Care Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConsultations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" py={3}>
                        No consultations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConsultations.map((consultation) => (
                    <TableRow key={consultation._id} sx={therapistStyles.tableRow}>
                      <TableCell>
                        <Typography variant="body2">
                          {consultation.clientId?.firstName} {consultation.clientId?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {consultation.clientId?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(consultation.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(consultation.createdAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {consultation.completedAt ? (
                          <Typography variant="body2">
                            {Math.round((new Date(consultation.completedAt) - new Date(consultation.createdAt)) / 60000)} min
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Ongoing
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={consultation.status}
                          size="small"
                          icon={getStatusIcon(consultation.status)}
                          sx={{
                            ...therapistStyles.statusChip,
                            ...(consultation.status === 'active' ? therapistStyles.activeStatus : 
                                consultation.status === 'completed' ? {
                                  backgroundColor: '#66BB6A',
                                  color: '#ffffff'
                                } : {})
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {consultation.consultationNotes?.summary ? (
                          <Chip
                            icon={<NotesIcon />}
                            label="Saved"
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {consultation.careNotes?.length > 0 ? (
                          <Chip
                            label={consultation.careNotes.length}
                            size="small"
                            color="secondary"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View consultation">
                          <IconButton
                            onClick={() => handleViewConsultation(consultation._id)}
                            sx={{
                              color: '#FF8C00',
                              '&:hover': {
                                backgroundColor: 'rgba(255,140,0,0.08)'
                              }
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>
    </Box>
  );
}

export default ConsultationHistory;